import { useState, useCallback, useEffect, createContext, useContext, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSSE } from './use-sse.js';
import { showPushNotification } from '../services/push.js';
import { followsServer, loadPrefs } from '../services/notification-prefs.js';
import { pushEnabledHere, renewPush } from '../services/web-push-client.js';
import { useApiClient } from './use-api-client.js';
import type { PlatformEvent, Notification } from '../api/types.js';

const MAX_NOTIFICATIONS = 50;

let nextId = 0;

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  hasPermissionPending: (serverId: string, sessionId?: string) => boolean;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const api = useApiClient();

  // A push subscription the browser replaced since the last opening is sent to the API again
  useEffect(() => {
    void renewPush(api.push, loadPrefs()).catch(() => undefined);
  }, [api]);

  // A notification opens its decision with an `n` tag: record it (types the operator ignores get downgraded),
  // then drop the tag from the address
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tag = params.get('n');
    const serverId = location.pathname.match(/^\/servers\/([^/]+)/)?.[1];
    if (!tag || !/^[0-9a-f]{12}$/.test(tag) || !serverId) return;
    void api.notifications.opened(decodeURIComponent(serverId), tag).catch(() => undefined);
    params.delete('n');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
  }, [api, location.pathname, location.search, location.hash, navigate]);

  const handleEvent = useCallback((event: PlatformEvent) => {
    // This browser may follow only some servers (one Chrome profile per client): the rest is ignored here
    if (!followsServer(loadPrefs(), event.serverId)) return;
    const notification: Notification = {
      id: String(++nextId),
      event,
      read: false,
      createdAt: event.timestamp,
    };

    setNotifications((prev) => {
      const next = [notification, ...prev];
      return next.length > MAX_NOTIFICATIONS ? next.slice(0, MAX_NOTIFICATIONS) : next;
    });

    // With Web Push on this device, the service worker notifies; the page does not repeat it
    if (document.visibilityState !== 'visible' && !pushEnabledHere()) {
      showPushNotification(event);
    }
  }, []);

  useSSE(handleEvent);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const hasPermissionPending = useCallback(
    (serverId: string, sessionId?: string) => {
      return notifications.some(
        (n) =>
          !n.read &&
          n.event.type === 'session.permission_pending' &&
          n.event.serverId === serverId &&
          (sessionId === undefined || n.event.sessionId === sessionId),
      );
    },
    [notifications],
  );

  return (
    <NotificationsContext.Provider
      value={{ notifications, unreadCount, markRead, markAllRead, hasPermissionPending }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) throw new Error('useNotifications must be used within NotificationsProvider');
  return context;
}
